// frontend/src/components/dashboard/MetricCard.tsx
import React from 'react';

interface MetricCardProps {
  title: string;
  value: string | number;
  icon?: React.ReactNode;
  trend?: {
    value: number;
    isPositive: boolean;
  };
  color?: 'blue' | 'green' | 'orange' | 'purple' | 'red';
  loading?: boolean;
  onClick?: () => void;
}

/**
 * Componente para mostrar una tarjeta de métrica
 * Optimizado para carga rápida con skeleton loading
 */
export const MetricCard: React.FC<MetricCardProps> = ({
  title,
  value,
  icon,
  trend,
  color = 'blue',
  loading = false,
  onClick
}) => {
  const colorClasses = {
    blue: {
      bg: 'bg-blue-50',
      border: 'border-blue-200',
      text: 'text-blue-700',
      textBold: 'text-blue-900',
      icon: 'text-blue-200'
    },
    green: {
      bg: 'bg-green-50',
      border: 'border-green-200',
      text: 'text-green-700',
      textBold: 'text-green-900',
      icon: 'text-green-200'
    },
    orange: {
      bg: 'bg-orange-50',
      border: 'border-orange-200',
      text: 'text-orange-700',
      textBold: 'text-orange-900',
      icon: 'text-orange-200'
    },
    purple: {
      bg: 'bg-purple-50',
      border: 'border-purple-200',
      text: 'text-purple-700',
      textBold: 'text-purple-900',
      icon: 'text-purple-200'
    },
    red: {
      bg: 'bg-red-50',
      border: 'border-red-200',
      text: 'text-red-700',
      textBold: 'text-red-900',
      icon: 'text-red-200'
    }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg border-l-4 border-gray-300 p-6 shadow-sm animate-pulse">
        <div className="flex items-center justify-between">
          <div className="flex-1">
            <div className="h-4 bg-gray-200 rounded w-24 mb-3"></div>
            <div className="h-8 bg-gray-200 rounded w-32"></div>
          </div>
          <div className="w-12 h-12 bg-gray-200 rounded-full"></div>
        </div>
      </div>
    );
  }

  return (
    <div
      className={`${colorClasses[color].bg} rounded-lg shadow p-4 border ${colorClasses[color].border} relative overflow-hidden ${onClick ? 'cursor-pointer hover:shadow-md transition-shadow' : ''}`}
      onClick={onClick}
    >
      {icon && (
        <div className={`absolute right-2 top-2 w-20 h-20 ${colorClasses[color].icon} opacity-50`}>
          {icon}
        </div>
      )}
      <div className="relative">
        <p className={`text-xs ${colorClasses[color].text} font-medium mb-1`}>{title}</p>
        <p className={`text-3xl font-bold ${colorClasses[color].textBold}`}>{value}</p>

        {trend && (
          <div className={`flex items-center mt-2 text-sm ${trend.isPositive ? 'text-green-600' : 'text-red-600'}`}>
            <span className="mr-1">
              {trend.isPositive ? '↑' : '↓'}
            </span>
            <span>{Math.abs(trend.value)}%</span>
            <span className="text-gray-500 ml-1">vs mes anterior</span>
          </div>
        )}
      </div>
    </div>
  );
};
